import {
  FlaskConical,
  Droplets,
  Wheat,
  Factory,
  Milk,
  Zap,
  Flame,
  Paintbrush,
  CircleDot,
  Beaker,
  Leaf,
  Waves,
  type LucideIcon,
} from "lucide-react";

export interface Industry {
  /** URL-safe id, used for the Industries page anchor (e.g. /industries#chemicals). */
  slug: string;
  name: string;
  icon: LucideIcon;
  /** One-line summary used on the Home tiles. */
  blurb: string;
  /** Longer paragraph shown on the Industries page. */
  detail: string;
  /** Representative commodities/products we haul for this vertical. */
  examples: string[];
  /** Optional 4:3 tile photo (public path). Tiles without one render a brand gradient. */
  image?: string;
  /** Hidden from the site but kept in code so it's easy to switch back on. */
  hidden?: boolean;
}

/**
 * Industries served. PLACEHOLDER set based on the reference carriers (bulk/tank
 * verticals); confirm BB's actual industries (docs/DESIGN.md open items).
 */
const allIndustries: Industry[] = [
  {
    slug: "chemicals",
    name: "Chemicals",
    icon: FlaskConical,
    blurb: "Safe, compliant transport of bulk and packaged chemicals.",
    detail:
      "Bulk liquid chemicals demand the right equipment, endorsements, and paperwork on every load. Our HazMat-endorsed drivers and dedicated tankers move acids, solvents, and specialty chemicals with the documentation and tracking shippers expect.",
    examples: ["Acids & bases", "Solvents", "Specialty chemicals", "Surfactants"],
    image: "/brand/industries/chemicals-2.jpg",
  },
  {
    slug: "lubricants",
    name: "Lubricants & Base Oil",
    icon: Droplets,
    blurb: "Base oils, additives, and finished lubricants.",
    detail:
      "Lubricant supply chains depend on clean, dedicated equipment and careful handling. We haul base oils, additive packages, and finished lubricants - including transformer oils - with the compliance and live visibility blenders and distributors expect.",
    examples: ["Base oils", "Additives", "Finished lubricants", "Transformer oils"],
    image: "/brand/industries/fuel-energy.jpg",
  },
  {
    // Hidden: BB does not offer food-grade yet. Flip `hidden` to false to restore.
    hidden: true,
    slug: "food-grade",
    name: "Food-grade",
    icon: Milk,
    blurb: "Sanitary, food-grade tank hauling.",
    detail:
      "Food-grade freight is about cleanliness and traceability. Our sanitary tankers and wash documentation keep edible liquids and ingredients moving safely from producer to plant.",
    examples: ["Edible oils", "Sweeteners & syrups", "Dairy liquids", "Juices & concentrates"],
    image: "/brand/industries/food-grade.jpg",
  },
  {
    slug: "agriculture",
    name: "Agriculture",
    icon: Wheat,
    blurb: "Seasonal capacity for ag inputs and commodities.",
    detail:
      "Agriculture moves in seasons, and so do we. We add capacity when planting and harvest demand it, hauling liquid fertilizers and ag inputs to co-ops and processors across the heartland.",
    examples: ["Liquid fertilizers", "Crop inputs", "Vegetable oils", "Molasses"],
    image: "/brand/industries/agriculture-2.jpg",
  },
  {
    slug: "manufacturing",
    name: "Manufacturing",
    icon: Factory,
    blurb: "Inbound materials and outbound finished liquids.",
    detail:
      "Plants need raw materials in and finished product out without missing a beat. We run dedicated and on-demand tanker lanes that keep production lines fed and inventory moving.",
    examples: ["Process chemicals", "Resins & polymers", "Glycols", "Finished liquids"],
    image: "/brand/industries/manufacturing-2.jpg",
  },
  {
    // Hidden per owner request 2026-07-10. Flip `hidden` to false to restore.
    hidden: true,
    slug: "industrial-gas",
    name: "Industrial Gas",
    icon: Zap,
    blurb: "Specialized equipment for industrial gas freight.",
    detail:
      "Industrial and cryogenic gas freight calls for specialized equipment and carefully trained drivers. We handle these demanding loads with the right tankers and a safety-first discipline.",
    examples: ["Cryogenic liquids", "Compressed gases", "Nitrogen & oxygen", "CO\u2082"],
    image: "/brand/industries/industrial-gas.jpg",
  },
  {
    slug: "oil-gas",
    name: "Oil & Gas / Energy Services",
    icon: Flame,
    blurb: "Purpose-built lanes for glycols, drilling chemicals, and gas-processing fluids from wellhead to plant.",
    detail:
      "Energy services freight runs on tight schedules and unforgiving sites. We haul the glycols and treatment chemistries that keep wellheads, gas processors, and midstream operators running - delivered from wellhead to plant with the right equipment and paperwork.",
    examples: ["Triethylene glycol (TEG)", "Diethylene & monoethylene glycol", "Drilling & completion chemicals", "Gas-processing fluids"],
    image: "/brand/industries/oil-gas.jpg",
  },
  {
    slug: "rubber-tire",
    name: "Rubber & Tire",
    icon: CircleDot,
    blurb: "Reliable inbound supply of process oils, latexes, and tackifiers for tire and rubber-goods manufacturing.",
    detail:
      "Tire plants and rubber-goods manufacturers plan production around inbound deliveries. We keep process oils, latexes, and tackifiers arriving on schedule, coordinated with plant receiving so lines never wait on a truck.",
    examples: ["Process oils (TDAE)", "Latexes", "Tall oil rosin", "Tackifiers"],
    image: "/brand/industries/rubber-tire.jpg",
  },
  {
    slug: "paints-coatings",
    name: "Paints, Coatings & Adhesives",
    icon: Paintbrush,
    blurb: "Contamination-controlled tankers for resins, latexes, and specialty coating intermediates.",
    detail:
      "Coatings formulators and adhesive plants live and die by product purity. Our contamination-controlled tankers and wash discipline move resin solutions, latexes, and specialty intermediates without surprises in the sample jar.",
    examples: ["Resin solutions", "Latexes", "Tall oil rosin", "Kaolin clay slurries"],
    image: "/brand/industries/paints-coatings.jpg",
  },
  {
    slug: "personal-care",
    name: "Personal Care & Pharma",
    icon: Beaker,
    blurb: "Food-grade and pharma-grade wash discipline for glycols, humectants, and cosmetic intermediates.",
    detail:
      "Personal care and pharma supply chains demand documented wash certificates and clean equipment histories. We haul glycols, humectants, and cosmetic intermediates with the wash discipline and traceability quality teams sign off on.",
    examples: ["Propylene glycol (USP / food-grade)", "Triethanolamine (TEA)", "Humectants", "Cosmetic intermediates"],
    image: "/brand/industries/personal-care.jpg",
  },
  {
    slug: "bioenergy",
    name: "Bioenergy & Renewables",
    icon: Leaf,
    blurb: "Feedstock and byproduct hauling for the biofuel, biodiesel, and biomass processing value chain.",
    detail:
      "Renewable fuel producers need dependable capacity on both ends of the process. We move feedstocks in and byproducts out for biofuel, biodiesel, and biomass processors - a growing lane we're invested in.",
    examples: ["Biodiesel feedstocks", "Crude glycerin", "Biomass liquids", "Renewable process fluids"],
    image: "/brand/industries/bioenergy.jpg",
  },
  {
    slug: "water-treatment",
    name: "Water & Wastewater Treatment",
    icon: Waves,
    blurb: "Bulk treatment chemistries for municipal water plants, wastewater facilities, and industrial utilities.",
    detail:
      "Water and wastewater plants can't run out of treatment chemistry. We deliver biocides, silicates, and conditioning chemicals to municipal and industrial treatment facilities on dependable schedules, with the handling discipline these products require.",
    examples: ["Biocides", "Sodium silicate", "Water-conditioning chemicals", "Industrial utility chemistries"],
    image: "/brand/industries/water-treatment.jpg",
  },
];

/** Visible industries (hidden ones stay in `allIndustries` for easy restore). */
export const industries: Industry[] = allIndustries.filter((i) => !i.hidden);
